import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  finalize,
  from,
  mergeMap,
  of,
  Subject,
  Subscription,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { FolderItem } from 'src/app/api/services/folder';
import { TrackerService } from '../../logging/tracker.service';
import { ListItem } from './scrolling-list-state.service';
import { StorageApiSearch } from 'src/app/api/services/api-utils.service';

@Injectable({
  providedIn: 'root',
})
export class BatchActionHandlerService {
  private queue: ListItem<any>[] = [];
  private items: ListItem<any>[] = [];
  private queueSubscription: Subscription;
  private queueSubject = new Subject<{
    item: ListItem<any>;
    method: (item: ListItem<any>, args?: unknown) => Promise<unknown>;
    args?: unknown;
    operation?: ListOperation<any>;
  }>();
  private cancelSubject = new Subject<void>();
  private finishedSubject = new Subject<
    | { success: true }
    | { success: false; error: { message: string; error: any } }
  >();
  private operationsChangesSubject = new BehaviorSubject<void>(null);
  private currentItemIndex = 0;
  listOperations: ListOperations;
  private resetTimeout: any;
  private afterAllOperationsBatchActions: Set<() => Promise<void>> = new Set();
  constructor(private trackerService: TrackerService) {}

  init(operations: ListOperations): void {
    if (this.resetTimeout) {
      clearTimeout(this.resetTimeout);
    }
    if (!operations || !operations.operations?.length) {
      return;
    }
    if (this.queueSubscription) {
      this.queueSubscription.unsubscribe();
      this.queueSubscription = null;
    }
    operations.queueLimit = operations.queueLimit || 1;
    this.reset(operations);
    this.queueSubscription = this.initQueue();
  }

  batch<T>(
    items: ListItem<T>[],
    method: (item: ListItem<T>, args?: unknown) => Promise<unknown>,
    operation: ListOperation<T>,
    args?: unknown,
  ): void {
    if (!items || !items.length || !method) {
      this.finishedSubject.next({ success: true });
      return;
    }
    this.trackerService.batchOperationEvent(
      'Batch Operation Started',
      operation,
      items,
    );
    this.items = items;
    this.listOperations.totalItemCount += items.length;

    this.loadBatch<T>(method, args, operation);
  }

  cancel() {
    this.cancelSubject.next();
    this.finishedSubject.next({
      success: false,
      error: { message: 'Cancelled', error: null },
    });
    this.queueSubject.complete();
  }

  finished() {
    return this.finishedSubject.asObservable().pipe(
      take(1),
      finalize(() => {
        if (this.resetTimeout) {
          clearTimeout(this.resetTimeout);
        }
        this.resetTimeout = setTimeout(() => {
          this.reset(this.listOperations);
          this.resetTimeout = null;
        }, 2000);
      }),
    );
  }

  operationsChanges() {
    return this.operationsChangesSubject.asObservable();
  }

  private initQueue() {
    return this.queueSubject
      .pipe(
        takeUntil(this.cancelSubject),
        mergeMap(({ item, method, args, operation }) => {
          this.listOperations.activeOperation = operation;
          if (operation.afterOperationBatchActions) {
            this.afterAllOperationsBatchActions.add(
              operation.afterOperationBatchActions,
            );
          }
          return from(method(item, args)).pipe(
            tap(() => {
              if (operation.afterAction && !operation.groupBy) {
                (
                  operation.afterAction as (
                    item: ListItem<any>,
                    err: any,
                  ) => void
                )(item, null);
              }
            }),
            catchError((err) => {
              this.listOperations.hasErrors = true;
              this.listOperations.error = err;
              if (operation.afterAction && !operation.groupBy) {
                (
                  operation.afterAction as (
                    item: ListItem<any>,
                    err: any,
                  ) => void
                )(item, err);
              }
              return of(null);
            }),
            tap(() => {
              const index = this.queue.indexOf(item);
              if (index !== -1) {
                this.queue.splice(index, 1);
              }
              this.listOperations.completedItemCount++;
              this.listOperations.progress = Math.round(
                (this.listOperations.completedItemCount /
                  this.listOperations.totalItemCount) *
                  100,
              );
              this.listOperations.progress = Math.min(
                100,
                this.listOperations.progress,
              );
            }),
            tap(() => {
              this.loadBatch(method, args, operation);
            }),
          );
        }, this.listOperations.queueLimit),
      )
      .subscribe({
        next: async () => {
          if (
            this.listOperations.totalItemCount ===
            this.listOperations.completedItemCount
          ) {
            if (this.afterAllOperationsBatchActions.size > 0) {
              try {
                await Promise.all(
                  [...this.afterAllOperationsBatchActions].map((fn) => fn()),
                );
              } catch (err) {
                this.listOperations.hasErrors = true;
                this.listOperations.error = err;
              } finally {
                this.afterAllOperationsBatchActions.clear();
              }
            }
            this.finishedSubject.next(
              this.listOperations.hasErrors
                ? {
                    success: false,
                    error: {
                      message: this.listOperations.error.message,
                      error: this.listOperations.error,
                    },
                  }
                : { success: true },
            );
          }
        },
        error: (err) => {
          this.finishedSubject.next({
            success: false,
            error: { message: err.message, error: err },
          });
          this.queueSubject.complete();
        },
      });
  }

  private pushItem<T>(
    item: ListItem<T>,
    method: (item: ListItem<T>, args?: unknown) => Promise<unknown>,
    args?: unknown,
    operation?: ListOperation<T>,
  ) {
    this.queue.push(item);
    this.currentItemIndex++;
    this.queueSubject.next({ item, method, args, operation });
  }

  private loadBatch<T>(
    method: (item: ListItem<T>, args?: unknown) => Promise<unknown>,
    args?: unknown,
    operation?: ListOperation<T>,
  ) {
    while (
      this.queue.length < this.listOperations.queueLimit &&
      this.currentItemIndex < this.items.length
    ) {
      const item = this.items[this.currentItemIndex];
      this.pushItem(item, method, args, operation);
    }
  }

  private reset(operations: ListOperations): void {
    this.cancelSubject = new Subject<void>();
    this.finishedSubject = new Subject();
    operations.hasErrors = false;
    operations.error = null;
    operations.activeOperation = null;
    operations.progress = 0;
    operations.totalItemCount = 0;
    operations.completedItemCount = 0;
    this.currentItemIndex = 0;
    this.listOperations = operations;
    this.operationsChangesSubject.next();
    this.afterAllOperationsBatchActions.clear();
  }
}

export interface ListOperations<T = any> {
  operations: ListOperation<T>[];
  hasErrors?: boolean;
  error?: any;
  activeOperation?: ListOperation<T> | null;
  progress?: number;
  totalItemCount?: number;
  completedItemCount?: number;
  queueLimit?: number;
  getStorageSearchApi?: () => StorageApiSearch;
}

export type ListOperation<T> = {
  name: string;
  onClick?: (
    skipBeforeBatchAction: boolean,
    selection: ListItem<T>[],
    args?: any,
    storageApiSearch?: StorageApiSearch,
  ) => Promise<void>;
  beforeBatchAction?: (selected: ListItem<T>[]) => Promise<any>;
  showActionError?: (err?: any) => boolean;
  hidden?: (selected?: ListItem<T>[]) => boolean;
  filter?: (selected: ListItem<T>) => boolean;
  isDelete?: boolean;
  danger?: boolean;
  requireRole?: string;
  mainOperation?: boolean;
  /** Parent row only: renders {@link children} in bulk UI; no batch handler on this node. */
  submenu?: boolean;
  children?: ListOperation<T>[];
  /** Shown disabled in bulk UI; no `onClick` wiring. */
  uiOnly?: boolean;
  showIn?: ('single' | 'bulk')[];
  icon?: string;
  reloadList?: boolean;
  inProgress?: boolean;
  deepFilter?: boolean;
  afterOperationBatchActions?: () => Promise<void>;
  separator?: boolean;
} & (
  | {
      groupBy: true | keyof (T | FolderItem);
      actionCall: (
        selected: { items: ListItem<T>[]; [key: string]: any },
        args?: any,
      ) => Promise<any>;
      afterAction?: (
        selected: { items: ListItem<T>[]; [key: string]: any },
        error?: any,
      ) => void;
    }
  | {
      groupBy?: undefined;
      actionCall: (item: ListItem<T>, args?: any) => Promise<any>;
      afterAction?: (item: ListItem<T>, error?: any) => void;
    }
);
